import { RelendTooltip } from "@/components/relendTooltip"
import { StyleObject } from "@/utils/helpers"
import { Typography } from "@mui/material"
import BigNumber from "bignumber.js"
import React, { ReactNode } from "react"
import { SKELETON_VALUE } from "./constants"

export function collapsableUsd(value: string, maxLength: number) {
  const bn = new BigNumber(value)

  if (bn.isLessThan(0.01) && !bn.isLessThanOrEqualTo(new BigNumber(0))) {
    return "< $0.01"
  }

  const usdString = formatUsd(value)

  return (
    <RelendTooltip title={usdString}>
      <Typography>{usdString.length > maxLength ? `$${formatCompact(bn)}` : usdString}</Typography>
    </RelendTooltip>
  )
}

export function collapsableToken(value: string, decimals: number, maxLength: number) {
  const bn = new BigNumber(value)
  if (bn.isLessThan(0.0001) && !bn.isLessThanOrEqualTo(new BigNumber(0))) {
    return "< 0.0001"
  }

  const valString = formatToken(value, decimals) as string

  return (
    <RelendTooltip title={value}>
      <Typography>{valString.length > maxLength ? formatCompact(new BigNumber(valString)) : valString}</Typography>
    </RelendTooltip>
  )
}

export function formatExact(value: string | number | BigNumber) {
  const bignum = new BigNumber(value)
  return bignum.isNaN() ? "0" : bignum.toFormat()
}

export function formatToken(
  value: string | number | BigNumber,
  digits = 3,
  exactTip?: boolean,
  trim?: boolean,
  // by default we truncate for tokens
  round?: BigNumber.RoundingMode,
  exact?: boolean,
  typoStyle?: StyleObject,
): React.ReactNode {
  const bn = new BigNumber(value)
  if (exact) {
    return formatExact(value)
  }
  if (bn.isLessThan(1 / 10 ** digits) && !bn.isLessThanOrEqualTo(new BigNumber(0))) {
    return (
      <RelendTooltip title={formatExact(value)}>
        <Typography whiteSpace='nowrap' sx={typoStyle}>{`< ${1 / 10 ** digits}`}</Typography>
      </RelendTooltip>
    )
  }

  const contents = trim
    ? `${Number.parseFloat(bn.toFixed(digits))}`
    : bn.toFormat(digits, typeof round !== "undefined" ? round : BigNumber.ROUND_DOWN)

  if (bn.eq(0)) return "0"

  return exactTip ? (
    <RelendTooltip title={formatExact(value)}>
      <Typography>{contents}</Typography>
    </RelendTooltip>
  ) : (
    contents
  )
}

export function formatRoundedToken(value: string | number, rounded?: boolean): string {
  const bn = new BigNumber(value)
  if (bn.isLessThan(0.0001) && !bn.isLessThanOrEqualTo(new BigNumber(0))) {
    return "< 0.0001"
  }

  return bn.toFormat(
    !bn.isLessThan(1000) || rounded ? 0 : 2,
    4, // ROUND_HALF_UP
  )
}

export function formatUsd(
  value: string | number | BigNumber,
  digits = 2,
  omitPrefix?: boolean,
  rounded?: boolean,
  sigFigs?: number,
  noTrim?: boolean,
): string {
  const bn = new BigNumber(value)
  const neg = bn.isLessThan(0)
  const abs = bn.abs()

  // Keep existing special cases
  if (value === "0" && sigFigs)
    return `${neg ? SKELETON_VALUE : ""}${omitPrefix ? "" : "$"}${Number(abs).toPrecision(sigFigs + 1)}`
  if (sigFigs && bn.lt(0.1)) {
    return `${neg ? SKELETON_VALUE : ""}${omitPrefix ? "" : "$"}${Number(abs).toPrecision(sigFigs)}`
  }
  if (!noTrim && bn.isLessThan(0.000001) && !bn.isLessThanOrEqualTo(new BigNumber(0))) {
    return `< ${omitPrefix ? "" : "$"}0.01`
  }
  // When we have to do token price conversion into USD, we are often either too precise
  // or not precise enough to fully net a number back to 0. This accounts for that inaccuracy
  if (bn.abs().isLessThan(0.000000001)) {
    return `${omitPrefix ? "" : "$"}0${rounded ? "" : ".00"}`
  }

  // New logic for normal numbers
  if (!rounded && !sigFigs) {
    const leftSideDigits = abs.integerValue().toString().length
    const decimalPlaces = Math.max(2, 6 - leftSideDigits)
    return `${neg ? SKELETON_VALUE : ""}${omitPrefix ? "" : "$"}${abs.toFormat(decimalPlaces)}`
  }

  // Default case (when rounded is true or sigFigs is provided)
  return `${neg ? SKELETON_VALUE : ""}${omitPrefix ? "" : "$"}${abs.toFormat(rounded ? 0 : digits)}`
}

export function formatPercent(
  value: string | number | BigNumber,
  noTrim?: boolean,
  decimals: number = 2,
  limit: number = 0.01,
  tooltip?: boolean,
  style?: StyleObject,
): ReactNode {
  const bnPercent = new BigNumber(value)
  const absBnPercent = new BigNumber(value).abs()
  if (absBnPercent.isLessThan(limit) && !absBnPercent.isLessThanOrEqualTo(new BigNumber(0)) && tooltip) {
    return (
      <RelendTooltip title={bnPercent.multipliedBy(100).toFormat(8)}>
        <Typography sx={style} whiteSpace='nowrap' component='span'>
          {"< 0.01%"}
        </Typography>
      </RelendTooltip>
    )
  }

  return noTrim ? `${bnPercent.multipliedBy(100)}%` : `${bnPercent.multipliedBy(100).toFormat(decimals)}%`
}

export function formatCompact(value: BigNumber) {
  const formatter = Intl.NumberFormat("en", { notation: "compact" })
  return formatter.format(Number(value.toString()))
}

export function formatNumberToDigits(value: number, maxDigits?: number, minDigits?: number) {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  })
}
